import { useEffect } from "react";
import { PayloadAction } from "@reduxjs/toolkit";
import { SeaRouteTypesModel, SeaStatusesModel } from "entities/shared/seaCard.types";
import { ApiShipmentType } from "entities/shared/shipmentCard.types";
import { ShipmentModel } from "features/shared/shipmentCard.types";
import { useFilterContext } from "features/Filter";
import { usePanelViews } from "features/shared/usePanelViews.hook";
import { AirSavedCardModel } from "entities/AirSavedCard";
import { appStore } from "entities/App";
import { authStore } from "entities/Auth";
import { seaCardDeleteModalStore } from "entities/SeaCardDeleteModal";
import {
  isSeaSavedShipmentModel,
  SeaSavedCardModel,
  seaSavedCardStore,
  useFollowCardMutation,
  useSavedCardsQuery,
  useSeaDeleteCardMutation,
} from "entities/SeaSavedCard";
import { isSeaSearchShipmentModel, SeaSearchCardModel, seaSearchCardStore } from "entities/SeaSearchCard";
import { resolveDateValues } from "entities/shared/utils";
import { voidFunc } from "shared/lib/common.lib";
import { useAppDispatch, useAppSelector } from "shared/lib/redux";
import { Nullable } from "shared/lib/typeCheck.lib";
import { formatApiDate, formatFollowCardDate } from "shared/utils/dateTime.utils";

type ShipmentCardModel = SeaSavedCardModel | SeaSearchCardModel;

type FollowFunc = (card: SeaSearchCardModel) => void;
type AskDeletingCardFunc = (card: ShipmentCardModel) => void;
type DeleteCardHandler = (card: ShipmentCardModel) => void;
type CancelDeletingSavedCardFunc = () => void;
type DeleteCardModalHandlerFunc = () => void;
type UseSeaCardActionsReturnType = {
  deleteSeaCardHandler: DeleteCardHandler;
  deleteSeaCardModalHandler: DeleteCardModalHandlerFunc;
  cancelSeaCardDeleting: CancelDeletingSavedCardFunc;
  seaCardFollow: FollowFunc;
  isSeaCardFollowLoading: boolean;
  isSeaCardFollowButtonVisible: boolean;
  isSeaCardDeleting: boolean;
};

type UseSeaCardActionsParams = {
  cardToDelete?: Nullable<ShipmentModel>;
  setCardToDeleteAction?: (card: ShipmentModel) => {
    type: string;
    payload: ShipmentModel | null;
  };
  resetCardToDeleteAction?: () => {
    type: string;
  };
  removeFilteredCardAction: (
    cards: SeaSavedCardModel | AirSavedCardModel
  ) => PayloadAction<SeaSavedCardModel | AirSavedCardModel>;
  removeCardByTagAction: (
    cards: SeaSavedCardModel | AirSavedCardModel
  ) => PayloadAction<SeaSavedCardModel | AirSavedCardModel>;
  refreshTags: () => void;
};
export const useSeaCardActions = ({
  cardToDelete,
  setCardToDeleteAction,
  resetCardToDeleteAction,
  removeFilteredCardAction,
  removeCardByTagAction,
  refreshTags,
}: UseSeaCardActionsParams): UseSeaCardActionsReturnType => {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(authStore.selectors.isAuthenticated);

  const requestedFromUrl = useAppSelector(appStore.selectors.requestedFromUrl);
  const savedCards = useAppSelector(seaSavedCardStore.selectors.savedCards);
  const searchCards = useAppSelector(seaSearchCardStore.selectors.searchCards);
  const { showPreviousView, isCurrentViewOpenedCard } = usePanelViews();
  const { getCards, removeCardsCache } = useSavedCardsQuery({ queryKey: ["check-sea-existing-shipment"] });
  const { followCard, isCardFollowLoading } = useFollowCardMutation();
  const { deleteCard: deleteSavedCardRequest, isCardDeleting: isSavedCardDeleting } = useSeaDeleteCardMutation();
  const { setAllStatuses } = useFilterContext();

  // Asks the server for the current status counts instead of guessing them
  // on the client - after a follow or a delete this is the source of truth.
  const { getCards: getStatusCounts, countByStatus: refreshedStatusCounts } = useSavedCardsQuery({
    queryKey: ["sea-saved-status-counts"],
  });

  useEffect(() => {
    if (!refreshedStatusCounts) return;
    setAllStatuses(refreshedStatusCounts);
  }, [refreshedStatusCounts]);

  const refreshStatusCounts = () => {
    getStatusCounts({ limit: 1 });
  };

  const removeSavedCardFromUI = (card: SeaSavedCardModel) => {
    dispatch(seaSavedCardStore.actions.removeSavedCardAction(card));
    dispatch(removeFilteredCardAction(card));
    dispatch(removeCardByTagAction(card));
    refreshStatusCounts();
  };

  const resetDeleteModal = () => {
    dispatch(seaCardDeleteModalStore.actions.closeDeleteModalAction());
    if (resetCardToDeleteAction) {
      dispatch(resetCardToDeleteAction());
    }
  };

  const handleModalDeleting = () => {
    resetDeleteModal();
    if (isAuthenticated) {
      refreshTags();
    }
    if (isCurrentViewOpenedCard) {
      showPreviousView();
    }
  };

  const askDeletingCard: AskDeletingCardFunc = (card) => {
    dispatch(seaCardDeleteModalStore.actions.openDeleteCardModalAction());
    if (setCardToDeleteAction) {
      dispatch(setCardToDeleteAction(card));
    }
  };

  const createSeaCardDeleteHandlers = (): {
    deleteSeaCardHandler: DeleteCardHandler;
    deleteSeaCardModalHandler: DeleteCardModalHandlerFunc;
  } => {
    // case 1: opened from url (and clicked delete button)
    if (isCurrentViewOpenedCard && requestedFromUrl) {
      return {
        deleteSeaCardHandler: (card) => {
          askDeletingCard(card);
        },
        deleteSeaCardModalHandler: () => {
          if (!cardToDelete) {
            handleModalDeleting();
            return;
          }

          if (!isAuthenticated && isSeaSearchShipmentModel(cardToDelete)) {
            dispatch(seaSearchCardStore.actions.removeSearchCardAction(cardToDelete));
            handleModalDeleting();
            return;
          }

          if (isAuthenticated && isSeaSearchShipmentModel(cardToDelete) && cardToDelete.reference) {
            const existingSavedCard = savedCards.find((c) => c.reference === cardToDelete.reference);

            if (existingSavedCard) {
              deleteSavedCardRequest(existingSavedCard.id, {
                onSettled: () => {
                  removeSavedCardFromUI(existingSavedCard);
                  handleModalDeleting();
                },
              });
              return;
            }

            const existingSearchCard = searchCards.find((c) => c.reference === cardToDelete.reference);

            if (existingSearchCard) {
              dispatch(seaSearchCardStore.actions.removeSearchCardAction(cardToDelete));
              handleModalDeleting();
            }

            getCards(
              { limit: 1, where: { reference: cardToDelete.reference } },
              {
                onSettled: (response) => {
                  if (response?.length) {
                    deleteSavedCardRequest(response[0].id, {
                      onSettled: () => {
                        removeCardsCache();
                        handleModalDeleting();
                      },
                    });
                  }
                },
              }
            );
            return;
          }

          if (isSeaSavedShipmentModel(cardToDelete)) {
            removeSavedCardFromUI(cardToDelete);
            deleteSavedCardRequest(cardToDelete.id, {
              onSettled: () => handleModalDeleting(),
            });
          }
        },
      };
    }

    // case 2: clicked delete button on openned card or closed card
    if (!requestedFromUrl) {
      return {
        deleteSeaCardHandler: (card) => {
          askDeletingCard(card);
        },
        deleteSeaCardModalHandler: () => {
          if (isSeaSavedShipmentModel(cardToDelete)) {
            deleteSavedCardRequest(cardToDelete.id, {
              onSettled: () => {
                removeSavedCardFromUI(cardToDelete);
                handleModalDeleting();
              },
            });
            return;
          }
          if (isSeaSearchShipmentModel(cardToDelete)) {
            dispatch(seaSearchCardStore.actions.removeSearchCardAction(cardToDelete));
            handleModalDeleting();
            return;
          }
        },
      };
    }

    return {
      deleteSeaCardHandler: voidFunc,
      deleteSeaCardModalHandler: voidFunc,
    };
  };

  const follow: FollowFunc = (data) => {
    if (!data || !data?.reference) return;
    const { atd, etd, ata, eta, predictiveETA } = data;
    const { departureDate, arrivalDate } = resolveDateValues({ atd, etd, ata, eta, predictiveETA });
    const departureDateWithLocale = departureDate.value ? formatFollowCardDate(departureDate.value) : null;
    const arrivalDateWithLocale = arrivalDate.value ? formatFollowCardDate(arrivalDate.value) : null;

    followCard(
      {
        reference: data.reference,
        shipment_type: ApiShipmentType.SEA,
        location: {
          pin: data.currentPin,
          from: data.fromDirection ?? null,
          to: data.toDirection ?? null,
          // TODO: replace with API data
          transport: SeaRouteTypesModel.SEA,
        },
        type: data.referenceType,
        equipments_count: data.containersAmount,
        carrier_scac: data.getCarrierScac(),
        carrier_name: data.carrierName ?? null,
        status: data.status ?? SeaStatusesModel.UNKNOWN,
        size_type: JSON.stringify(data.containersSizeTypes),
        date: {
          from_actual: departureDate.isActual,
          from: departureDateWithLocale,
          to_actual: arrivalDate.isActual,
          to: arrivalDate.isPredictive ? null : arrivalDateWithLocale,
          to_predictive: arrivalDate.isPredictive ? arrivalDate.value : null,
        },
        updated_at: formatApiDate(new Date()),
      },
      {
        onSuccess: (response) => {
          dispatch(seaSearchCardStore.actions.removeSearchCardAction(data));
          dispatch(seaSavedCardStore.actions.prependSavedCardAction({ data: response }));
          refreshStatusCounts();
        },
      }
    );
  };

  const cancelSeaCardDeleting: CancelDeletingSavedCardFunc = () => {
    if (isSavedCardDeleting) return;
    resetDeleteModal();
  };

  return {
    ...createSeaCardDeleteHandlers(),
    cancelSeaCardDeleting,
    seaCardFollow: follow,
    isSeaCardFollowLoading: isCardFollowLoading,
    // Every search card in the list shares this one hook, so keying the button
    // off the mutation's result hid it on all the others as soon as one card
    // was followed. A search card is by definition not saved yet, so being
    // signed in is the whole condition.
    isSeaCardFollowButtonVisible: isAuthenticated,
    isSeaCardDeleting: isSavedCardDeleting,
  };
};
